<!--
Props:
- `title` (String): Modal title. Default: ''.
- `description` (String): Modal description. Default: ''. Supports HTML (rich text).
- `hasInfoButton` (Boolean): Show open button (?). Default: true.
- `modalOpen` (Boolean): Initial open state. Default: false. Animates when toggeled.
- `hasCloseButton` (Boolean): Show close button (x). Default: true.
- `closesOnOverlayClick` (Boolean): Close modal when clicking outside the modal. Default: true.

Slots:
- Default: Custom content inside the modal.

Events:
- `handleModalClose`: Emitted when the modal closes.
-->

<template>
  <div class="modal">
    <button v-if="hasInfoButton" class="icon-button" @click="toggleModal" aria-label="Open modal">
      ?
    </button>
    <transition name="modal" :duration="350">
      <div v-if="isModalOpen" class="modal-container" role="presentation">
        <div
          class="modal-content"
          role="dialog"
          aria-labelledby="modalTitle"
          aria-describedby="modalDescription"
        >
          <div class="modal-inner">
            <div class="modal-top" v-if="title && hasCloseButton">
              <div id="modalTitle" class="modal-title" v-if="title">{{ title }}</div>
              <button
                v-if="hasCloseButton"
                class="button-close"
                @click="toggleModal"
                aria-label="Close modal"
              >
                <span class="x-line"></span>
                <span class="x-line"></span>
              </button>
            </div>
            <div
              id="modalDescription"
              class="modal-description"
              v-if="description"
              v-html="description"
            ></div>
            <slot></slot>
          </div>
        </div>
        <div class="modal-overlay" @click.self="handleOverlayClick"></div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  props: {
    title: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    hasInfoButton: {
      type: Boolean,
      default: true,
    },
    modalOpen: {
      type: Boolean,
      default: false,
    },
    hasCloseButton: {
      type: Boolean,
      default: true,
    },
    closesOnOverlayClick: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isModalOpen: this.modalOpen,
    }
  },
  watch: {
    modalOpen(value) {
      this.isModalOpen = value
    },
  },
  methods: {
    toggleModal() {
      this.isModalOpen = !this.isModalOpen
      if (!this.isModalOpen) {
        this.$emit('close') // Emit a "close" event to the parent component
      }
    },
    handleOverlayClick() {
      if (this.closesOnOverlayClick) {
        this.toggleModal()
      }
    },
    handleKeydown(event) {
      if (event.key === 'Escape' && this.isModalOpen) {
        this.isModalOpen = false
      }
    },
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeydown)
  },
  updated() {
    if (this.isModalOpen) {
      this.$nextTick(() => {
        const modal = this.$el.querySelector('.modal-overlay')
        modal.setAttribute('tabindex', '-1')
      })
    }
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeydown)
  },
}
</script>
<style lang="postcss" scoped>
.modal {
  display: flex;
  justify-content: flex-end;
}

.modal-container {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 30;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal-overlay {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  background-color: #00196599;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: opacity 0.35s ease-in-out;
}

.modal-content {
  width: 100%;
  max-height: 100dvh;
  position: relative;
  background: var(--color-white);
  border-radius: 18px;
  z-index: 10;
  padding: 32px 22px;
  margin-right: var(--grid-gutter-mobile);
  margin-left: var(--grid-gutter-mobile);
  transform: translateX(0%);
  transition: transform 0.35s ease-in-out;
  outline: none; /* Prevent browser default focus outline */
  max-width: 656px;
}

.modal-inner {
  max-height: calc(95dvh - 64px);
  overflow-y: auto;
  width: 100%;
  scrollbar-width: auto;
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.icon-button {
  width: 20px;
  height: 20px;
  background-color: var(--color-blue);
  border-radius: 999px;
  border: 1px solid var(--color-green);
  color: var(--color-white);
  margin-right: 12px;
}

.button-close {
  position: relative;
  z-index: 20;
  width: 28px;
  height: 28px;
  background-color: var(--color-blue);
  border-radius: 999px;
  flex-shrink: 0;
}

.x-line {
  position: absolute;
  width: 10px;
  height: 1px;
  border-radius: 1px;
  background-color: var(--color-white);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(45deg);

  & + .x-line {
    transform: translate(-50%, -50%) rotate(-45deg);
  }
}

.modal-top {
  display: flex;
  justify-content: space-between;
  gap: var(--grid-gutter-mobile);
}

.modal-title {
  font-family: var(--font-it-ex);
  font-size: 20px;
}

.modal-description {
  margin-bottom: 16px;
  font-weight: 400;
  font-family: var(--font-it-ex);
  font-size: var(--font-size-body);
}

@media (min-width: 1024px) {
  .modal-title {
    font-weight: 700;
    font-size: 24px;
  }

  .modal-description {
    font-size: var(--font-size-body);
  }
}

/* Default: Slide animation for smaller screens */
.modal-enter-active .modal-content,
.modal-leave-active .modal-content {
  transform: translateX(0);
  transition:
    transform 0.3s ease-in-out,
    opacity 0.3s ease-in-out;
}

.modal-enter-from .modal-content,
.modal-leave-to .modal-content {
  transform: translateX(calc(50vw + 50%));
  opacity: 0;
}

/* Fade animation for the overlay (applies to all screen sizes) */
.modal-enter-active .modal-overlay,
.modal-leave-active .modal-overlay {
  opacity: 1;
  transition: opacity 0.15s ease-in-out;
}

.modal-enter-from .modal-overlay,
.modal-leave-to .modal-overlay {
  opacity: 0;
}

/* Fade & Pop-Up animation for desktops */
@media (min-width: 1024px) {
  .modal-enter-active .modal-content,
  .modal-leave-active .modal-content {
    opacity: 1;
    transform: translateY(0);
    transition:
      opacity 0.2s ease-in-out,
      transform 0.2s ease-in-out;
  }

  .modal-enter-from .modal-content {
    opacity: 0;
    transform: translateY(1rem);
  }

  .modal-leave-to .modal-content {
    opacity: 0;
    transform: translateY(1rem);
  }
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  border: 0;
  padding: 0;
  clip: rect(0, 0, 0, 0);
  overflow: hidden;
}

@media (min-width: 1200px) {
  .modal-content {
    width: 600px;
    padding: 28px 28px 28px 28px;
  }

  .modal-inner {
    max-height: calc(95dvh - 128px);
    overflow-y: auto;
  }
}
</style>
